import { v4 as uuidv4 } from 'uuid';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { types as commonTypes, localization as commonLocalization } from '@sitebuilder/common';
import { DocumentClass, types as coreTypes } from '@sitebuilder/core';
import { ReferenceComponentApiClient } from '../api/referenceComponentsApiClient';
import { PublicComponentApiClient } from '../api/publicComponentsApiClient';
// eslint-disable-next-line max-len
import { onClickClose as closeColorPicker } from '../components/BaseComponents/ThemeColorPicker';
import { ResourceApiClient } from '../api/resourceApiClient';

import { resourcesReducerActions } from './resources';
import { ThunkResult } from '.';

export interface StateShape {
    selectedId: string | null;
    document: coreTypes.Document | coreTypes.ReferenceComponent | null;
    isDocumentLoaded?: boolean;
    data: any;
    documentMetadata: coreTypes.MetaDataOptions;
    editor?: coreTypes.Editor;
    pageLoading?: boolean;
    saveNewPageLoading?: boolean;
    pageSaveError?: string | null;
    pageSaveSuccess?: string | null;
    isShowAnnotationPicker?: boolean;
    selectedAnnotationPickerValue?: string | null;
    isShowDataPicker?: boolean;
    isShowFundsPicker?: boolean;
    newFolderSaveLoading?: boolean;
    newFolderSaveError?: any | null;
    newFolderSaveSucess?: string | null;
    bottomBarTabs: string[];
    pageResourcesAction?: string;
    snapshots?: Record<string, string>;
}

interface CreateComponentParams {
    widgetFactory: commonTypes.IWidgetFactory;
    componentId: string;
    parentId: string;
    contentPlaceHolderId: string;
    index: number;
    widgetUUID?: string;
    isRefComponent?: boolean;
    isPublicRefComponent?: boolean;
}

interface DuplicateCreateComponentParams {
    parentId: string;
    contentPlaceHolderId: string;
    index: number;
    widgetUUID: string;
}

interface MoveComponentParams {
    parentId: string;
    contentPlaceHolderId: string;
    index: number;
    widgetUUID: string;
}

const defaultState: StateShape = {
    selectedId: null,
    document: null,
    data: null,
    isDocumentLoaded: false,
    documentMetadata: {
        pageTitle: '',
        locale: 'en-US',
    },
    editor: null,
    pageLoading: false,
    saveNewPageLoading: false,
    pageSaveError: null,
    pageSaveSuccess: null,
    isShowAnnotationPicker: false,
    selectedAnnotationPickerValue: null,
    newFolderSaveLoading: false,
    newFolderSaveError: null,
    newFolderSaveSucess: '',
    bottomBarTabs: [],
    pageResourcesAction: '',
    isShowFundsPicker: false,
    snapshots: {},
};

const resourcesApiClient = new ResourceApiClient();

export const actionCreators = {
    createComponent: (params: CreateComponentParams): ThunkResult<Promise<void>> => {
        return async (dispatch, _getState) => {
            // unique identifier of the new instance
            const id = uuidv4();
            const {
                componentId,
                parentId,
                contentPlaceHolderId,
                index,
                isRefComponent,
                isPublicRefComponent,
            } = params;
            // initializing api service
            const apiClient = new ReferenceComponentApiClient();
            const publicCompApiClient = new PublicComponentApiClient();
            const _componentId =
                isRefComponent || isPublicRefComponent ? 'ReferenceComponent' : componentId;

            const widget = params.widgetFactory.getWidget(_componentId);

            if (!widget) {
                return;
            }

            // Get root document properties for inheritance (only for Page widgets)
            let initialData;
            if (widget.id === 'Page') {
                const state = _getState();
                const rootDocumentProperties = state.templateEditor.document?.widgetInstances?.root?.properties || {};
                initialData = (widget.initialData as (context?: Record<string, unknown>) => Record<string, unknown>)({
                    rootDocumentProperties
                });
            } else {
                initialData = widget.initialData();
            }
            const initialProperties =
                isRefComponent || isPublicRefComponent
                    ? { ...initialData, referenceId: componentId, isPublicRefComponent }
                    : initialData;
            const widgetInstance: coreTypes.WidgetInstance = {
                id,
                contentPlaceHolders: widget.contentPlaceHolders.reduce(
                    (memo, x) => ({ ...memo, [x]: [] }),
                    {}
                ),
                parentId,
                properties: initialProperties,
                widgetId: widget.id,
            };
            dispatch(
                templateSlice.actions.COMPONENT_INSERTED({
                    contentPlaceHolderId,
                    index,
                    widgetInstance,
                })
            );

            if (isRefComponent || isPublicRefComponent) {
                const breakString = componentId.lastIndexOf('/');
                const libraryId = componentId.substr(0, breakString);
                const modifyComponentId = componentId.substr(breakString + 1);
                const state = _getState();
                const accessToken = state.auth.user?.access_token;
                let referenceComponent: any = null;
                if (isRefComponent) {
                    referenceComponent = await apiClient.getComponent(
                        libraryId,
                        modifyComponentId,
                        { accessToken }
                    );
                } else if (isPublicRefComponent) {
                    referenceComponent = await publicCompApiClient.getPublicComponent(
                        libraryId,
                        modifyComponentId,
                        { accessToken }
                    );
                }

                dispatch(
                    templateSlice.actions.SET_REFERENCE_COMPONENT({
                        id,
                        referenceComponent,
                    })
                );
            }
        };
    },

    createDuplicateComponent: (
        params: DuplicateCreateComponentParams
    ): ThunkResult<Promise<void>> => {
        const { parentId, contentPlaceHolderId, index, widgetUUID } = params;
        return async (dispatch, _getState) => {
            if (widgetUUID) {
                dispatch(
                    templateSlice.actions.DUPLICATE_COMPONENT({
                        parentId,
                        contentPlaceHolderId,
                        index,
                        widgetUUID,
                        isCopyElement: true,
                    })
                );
            }
        };
    },

    moveComponent: (params: MoveComponentParams): ThunkResult<Promise<void>> => {
        const { parentId, contentPlaceHolderId, index, widgetUUID } = params;
        return async (dispatch, _getState) => {
            if (widgetUUID) {
                dispatch(
                    templateSlice.actions.DUPLICATE_COMPONENT({
                        parentId,
                        contentPlaceHolderId,
                        index,
                        widgetUUID,
                        isCopyElement: false,
                    })
                );
            }
        };
    },

    moveSlide:
        (slideId: string, newIndex: number): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(
                templateSlice.actions.MOVE_SLIDE({
                    slideId,
                    newIndex,
                })
            );
        },

    removeSelected: (): ThunkResult<Promise<void>> => async (dispatch, getState) => {
        const state = getState().templateEditor;

        if (!state.document || !state.selectedId) return;

        const widgetInstances = state.document.widgetInstances;

        const descendantsAndSelf = travereTreeDepthFirst(widgetInstances, state.selectedId);

        for (const did of descendantsAndSelf) {
            dispatch(templateSlice.actions.COMPONENT_REMOVED(did));
        }
    },

    saveWidgetProperties:
        (
            id: string,
            properties: Record<string, unknown>,
            editorType: coreTypes.Editor
        ): ThunkResult<Promise<void>> =>
        async (dispatch, getState) => {
            const { document } = getState().templateEditor;
            const refId = document?.widgetInstances?.[id]?.refId;
            if (editorType !== 'componentEditor' && refId) {
                dispatch(
                    templateSlice.actions.SAVE_REFERENCE_COMPONENT_PROPERTIES({
                        id,
                        refId,
                        properties,
                    })
                );
            } else {
                dispatch(templateSlice.actions.SAVE_COMPONENT_PROPERTIES({ id, properties }));
            }
        },
    selectElement:
        (id: string): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.WIDGET_SELECTED(id));
        },

    deselectElement: (): ThunkResult<Promise<void>> => async (dispatch) => {
        dispatch(resourcesReducerActions.SET_IS_SHOW_RESOURCE_PICKER(false));
        dispatch(templateSlice.actions.WIDGET_DESELECTED());
    },

    emptyData: (): ThunkResult<Promise<void>> => async (dispatch) => {
        dispatch(templateSlice.actions.EMPTY_DATA(null));
    },

    setPageAction:
        (pageResourcesAction: string): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_PAGE_ACTION(pageResourcesAction));
        },

    loadDocument:
        (documentName: string): ThunkResult<Promise<coreTypes.Document | undefined>> =>
        async (dispatch, _getState) => {
            const state = _getState();
            const { resourcePickerValues } = state.resources;
            dispatch(templateSlice.actions.FETCH_DOCUMENT());
            dispatch(templateSlice.actions.IS_DOCUMENT_LOADED(false));

            try {
                const url = `/api/reports/${documentName}`;
                // only absolute URLs are allowed!
                const results = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `bearer ${state.auth.user?.access_token}`,
                    },
                    // body: JSON.stringify({ query, variables }
                });
                const document = await results.json();
                const siteResources = await resourcesApiClient.getResources();

                // need to remove this logic once all the page resources are converted
                // to new format across all the clients
                const resourceStrings: any =
                    document?.widgetInstances?.root?.properties?.resourceStrings || null;
                const resources: any = {};

                if (resourceStrings && Array.isArray(resourceStrings)) {
                    resourceStrings?.forEach((rString: any) => {
                        Object.keys(rString)
                            ?.filter((key: string) => key !== 'id')
                            ?.forEach((k: string) => {
                                const locale = k === 'en' ? 'en-US' : k === 'fr' ? 'fr-CA' : k;
                                resources[locale] = {
                                    ...resources[locale],
                                    [rString.id]: rString[k],
                                };
                            });
                    });
                    document.widgetInstances.root.properties.resourceStrings = resources;
                }

                dispatch(templateSlice.actions.RECEIVE_DOCUMENT(document));
                dispatch(
                    resourcesReducerActions.SET_RESOURCE_PICKER_VALUES({
                        ...resourcePickerValues,
                        page: Object.keys(resources).length === 0 ? resourceStrings : resources,
                        site: siteResources,
                    })
                );

                dispatch(templateSlice.actions.IS_DOCUMENT_LOADED(true));

                return document;
            } catch (err) {
                console.error(err);
                dispatch(templateSlice.actions.FETCH_DOCUMENT_FAILED({ error: err }));
                dispatch(templateSlice.actions.IS_DOCUMENT_LOADED(true));
            }
        },

    saveDocument:
        (documentName: string, viewUrl: string): ThunkResult<Promise<void>> =>
        async (dispatch, getState) => {
            const state = getState();
            const { document } = state.templateEditor;
            dispatch(templateSlice.actions.SAVE_DOCUMENT({ documentName }));

            try {
                const url = `/api/admin/reports/${documentName}`;
                await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `bearer ${state.auth.user?.access_token}`,
                    },
                    body: JSON.stringify({ document, viewUrl }),
                });
            } catch (err) {
                dispatch(templateSlice.actions.SAVE_DOCUMENT_FAILED({ documentName, error: err }));
            }
        },

    saveNewFolder: (folderName: string): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            dispatch(templateSlice.actions.NEWFOLDER_SAVE_START());

            try {
                const url = `/api/admin/reports/create-folder/${folderName}`;
                await fetch(url, {
                    method: 'POST',
                    headers: {
                        'ldr-type': 'collection',
                        'Content-Type': 'application/json',
                        Authorization: `bearer ${state.auth.user?.access_token}`,
                    },
                });
                dispatch(
                    templateSlice.actions.NEWFOLDER_SAVE_END({
                        success: appI18n.t('folder_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    templateSlice.actions.NEWFOLDER_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },

    saveNewPage:
        (pageId: string, selectedTemplate?: string): ThunkResult<Promise<void>> =>
        async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);
            const document = {
                widgetInstances: {
                    root: {
                        widgetId: 'document',
                        parentId: '',
                        id: 'root',
                        properties: {},
                        contentPlaceHolders: {
                            children: [],
                        },
                    },
                },
                documentAttributes: {},
            };
            dispatch(templateSlice.actions.SAVE_NEW_PAGE_START());

            try {
                const url = `/api/admin/reports/${pageId}`;
                await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `bearer ${state.auth.user?.access_token}`,
                    },
                    body: JSON.stringify({ document, selectedTemplate }),
                });
                dispatch(
                    templateSlice.actions.SAVE_NEW_PAGE_END({
                        success: appI18n.t('page_form_save_success'),
                    })
                );
            } catch (err) {
                dispatch(templateSlice.actions.SAVE_NEW_PAGE_END({ error: err }));
            }
        },

    setData:
        (data: object): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_DATA(data));
        },

    setDocumentMetaData:
        (metaData: coreTypes.MetaDataOptions): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_DOCUMENT_META_DATA(metaData));
        },
    setEditor:
        (editor: coreTypes.Editor): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_EDITOR(editor));
        },
    savePageResources:
        (resources: coreTypes.Resource): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SAVE_PAGE_RESOURCES(resources));
        },
    setIsShowAnnotationPicker:
        (value: boolean): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_IS_SHOW_ANNOTATION_PICKER(value));
            dispatch(templateSlice.actions.SET_SELECTED_ANNOTATION_PICKER_VALUE(null));
        },
    setSelectedAnnotationPickerValue:
        (value: string | null): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_SELECTED_ANNOTATION_PICKER_VALUE(value));
        },
    setIsShowDataPicker:
        (value: boolean): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_IS_SHOW_DATA_PICKER(value));
        },
    setIsShowFundsPicker:
        (value: boolean): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_IS_SHOW_FUNDS_PICKER(value));
        },
    addBottomBarTab:
        (tab: string): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.ADD_BOTTOM_BAR_TAB(tab));
        },
    closeBottomBarTab:
        (tab: string): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.REMOVE_BOTTOM_BAR_TAB(tab));
        },
    closeAllBottomBarTabs: (): ThunkResult<Promise<void>> => async (dispatch) => {
        dispatch(templateSlice.actions.REMOVE_ALL_BOTTOM_BAR_TABS());
    },
    updateSlideSnapshot:
        (id: string, snapshot: string): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.UPDATE_SLIDE_SNAPSHOT({ id, snapshot }));
        },
};

// breath first tranversal of the component tree
export function travereTreeDepthFirst(
    componentTree: Record<string, coreTypes.WidgetInstance>,
    nodeId = 'root',
    filter = (_: string) => true
) {
    const queue = [];
    const results = [];
    let nextNodeId;
    queue.push(nodeId);

    while (queue.length > 0) {
        nextNodeId = queue.shift();

        if (typeof nextNodeId === 'undefined') continue;

        if (filter(nextNodeId) && componentTree[nextNodeId]?.contentPlaceHolders) {
            // results.push(nextNodeId);
            const conentPlaceHolderIds = Object.keys(componentTree[nextNodeId].contentPlaceHolders);

            for (let i = 0; i < conentPlaceHolderIds.length; i++) {
                const contentPlaceHolderId = conentPlaceHolderIds[i];
                const children: string[] =
                    componentTree[nextNodeId].contentPlaceHolders[contentPlaceHolderId];

                if (Array.isArray(children)) {
                    for (let j = 0; j < children.length; j++) {
                        queue.push(children[j]);
                    }
                }
            }

            results.push(nextNodeId);
        }
    }

    return results;
}

export function removeComponent(draft: Draft<StateShape>, widgetUUID: string) {
    const widgetInstances = draft.document?.widgetInstances || {};
    const widgetInstance = widgetInstances[widgetUUID];

    if (!draft.document || !widgetInstance) return;

    let widgetInstanceId = widgetInstance.id;
    const parentId = widgetInstance.parentId;
    const parent = draft.document.widgetInstances[parentId];
    const refId = widgetInstance.refId;

    // Clean from parent placeholders
    if (parentId) {
        parent.contentPlaceHolders = Object.keys(parent.contentPlaceHolders).reduce(
            (memo, key) => ({
                ...memo,
                [key]: parent.contentPlaceHolders[key].filter(
                    (id: string) => id !== widgetInstanceId
                ),
            }),
            {}
        );
    }

    // Deselect if selected
    if (draft.selectedId === widgetInstanceId) {
        draft.selectedId = null;
    }

    // Remove component and all descendants
    const instancesToRemove = [widgetUUID];

    while (instancesToRemove.length > 0) {
        const toRemove = instancesToRemove.shift();
        if (!toRemove) continue;

        const currentInstance = widgetInstances[toRemove];
        if (!currentInstance) continue;

        delete draft.document.widgetInstances[toRemove];

        for (const key of Object.keys(currentInstance.contentPlaceHolders)) {
            for (const childId of currentInstance.contentPlaceHolders[key]) {
                instancesToRemove.push(childId);
            }
        }
    }

    // Handle reference-based components (customizations)
    if (refId) {
        widgetInstanceId = `${refId}/${widgetInstance.id}`;
        draft.document.widgetInstances[widgetInstanceId] = {
            id: widgetInstanceId,
            widgetId: widgetInstance.widgetId,
            parentId: widgetInstance.parentId,
            properties: widgetInstance.properties,
            contentPlaceHolders: widgetInstance.contentPlaceHolders,
            refId,
        };

        draft.document.widgetInstances[refId] = {
            ...draft.document.widgetInstances[refId],
            customizations: {
                ...draft.document.widgetInstances[refId]?.customizations,
                [widgetInstance.parentId]: {
                    ...draft.document.widgetInstances[refId]?.customizations?.[
                        widgetInstance.parentId
                    ],
                    contentPlaceHolders: parent.contentPlaceHolders,
                },
            },
        };
    }
}

const templateSlice = createSlice({
    name: 'templateEditor',
    initialState: defaultState,
    reducers: {
        SET_DATA(draft, action) {
            draft.data = action.payload;
        },

        WIDGET_SELECTED(draft, action: PayloadAction<string>) {
            draft.selectedId = action.payload;
        },

        WIDGET_DESELECTED(draft) {
            draft.isShowAnnotationPicker = false;
            draft.isShowDataPicker = false;
            closeColorPicker();
            draft.selectedId = null;
        },

        COMPONENT_INSERTED(
            draft,
            action: PayloadAction<{
                contentPlaceHolderId: string;
                index: number;
                widgetInstance: coreTypes.WidgetInstance;
            }>
        ) {
            if (!draft.document) {
                return;
            }

            const { contentPlaceHolderId, index, widgetInstance } = action.payload;
            const refLoaded = draft.document.widgetInstances?.[widgetInstance?.parentId]?.refLoaded;
            let widgetInstanceId = widgetInstance.id;

            draft.document.widgetInstances[widgetInstanceId] = {
                id: widgetInstanceId,
                widgetId: widgetInstance.widgetId,
                parentId: widgetInstance.parentId,
                properties: widgetInstance.properties,
                contentPlaceHolders: widgetInstance.contentPlaceHolders,
            };

            const parent = draft.document.widgetInstances[widgetInstance.parentId];

            if (!parent.contentPlaceHolders[contentPlaceHolderId]) {
                parent.contentPlaceHolders = {
                    ...parent.contentPlaceHolders,
                    [contentPlaceHolderId]: [],
                };
            }
            const placeholder = parent.contentPlaceHolders[contentPlaceHolderId];

            if (action.payload.index === -1) {
                placeholder.push(widgetInstanceId);
            } else {
                placeholder.splice(index, 0, widgetInstanceId);
            }

            if (refLoaded) {
                const refId = widgetInstance?.parentId;
                widgetInstanceId = `${refId}/${widgetInstance.id}`;
                draft.document.widgetInstances[widgetInstanceId] = {
                    id: widgetInstanceId,
                    widgetId: widgetInstance.widgetId,
                    parentId: widgetInstance.parentId,
                    properties: widgetInstance.properties,
                    contentPlaceHolders: widgetInstance.contentPlaceHolders,
                    refId,
                };

                draft.document.widgetInstances[refId] = {
                    ...draft.document.widgetInstances[refId],
                    customizations: {
                        ...draft.document.widgetInstances[refId]?.customizations,
                        [widgetInstance.parentId]: {
                            ...draft.document.widgetInstances[refId]?.customizations?.[
                                widgetInstance.parentId
                            ],
                            contentPlaceHolders: parent.contentPlaceHolders,
                        },
                    },
                };
            }
        },
        COMPONENT_REMOVED(draft, action: PayloadAction<string>) {
            removeComponent(draft, action.payload);
        },
        DUPLICATE_COMPONENT(
            draft,
            action: PayloadAction<{
                widgetUUID: string;
                index: number;
                parentId: string;
                contentPlaceHolderId: string;
                isCopyElement: boolean;
            }>
        ) {
            if (!draft.document) return;

            const widgetInstances = draft.document.widgetInstances;
            const widgetsToCreate = [
                {
                    id: action.payload.widgetUUID,
                    parentId: action.payload.parentId,
                    contentPlaceHolderId: action.payload.contentPlaceHolderId,
                    index: action.payload.index,
                },
            ];

            while (widgetsToCreate.length > 0) {
                const wtc = widgetsToCreate.shift();

                if (!wtc) continue;

                const srcInstance = widgetInstances[wtc.id];

                if (!srcInstance) continue;

                // createa a new unique id for the new instnace
                const uuid = uuidv4();

                // Adding child widget ids to childWidgetsToCreate
                // push children to the queue
                const srcPlaceHolders = srcInstance.contentPlaceHolders;
                const srcPlaceHolderIds = Object.keys(srcPlaceHolders);
                const newContentPlaceHolders = srcPlaceHolderIds.reduce(
                    (memo: Record<string, string[]>, x) => ({ ...memo, [x]: [] }),
                    {}
                );

                for (const srcPlaceHolderId of srcPlaceHolderIds) {
                    const cph = srcPlaceHolders[srcPlaceHolderId];

                    if (Array.isArray(cph) && cph.length > 0) {
                        for (let i = 0; i < cph.length; i++) {
                            const childId = cph[i];
                            widgetsToCreate.push({
                                id: childId,
                                parentId: uuid,
                                contentPlaceHolderId: srcPlaceHolderId,
                                index: i,
                            });
                        }
                    }
                }

                draft.document.widgetInstances[uuid] = {
                    id: uuid,
                    widgetId: srcInstance.widgetId,
                    parentId: wtc.parentId,
                    properties: srcInstance.properties,
                    contentPlaceHolders: newContentPlaceHolders,
                };

                // updating the parent widgets
                const parent = draft.document.widgetInstances[wtc.parentId];
                const placeholder = parent.contentPlaceHolders[wtc.contentPlaceHolderId];

                if (action.payload.index === -1) {
                    placeholder.push(uuid);
                } else {
                    placeholder.splice(wtc.index, 0, uuid);
                }
            }

            if (!action.payload.isCopyElement) {
                removeComponent(draft, action.payload.widgetUUID);
            }
        },
        MOVE_SLIDE(
            draft,
            action: PayloadAction<{
                slideId: string;
                newIndex: number;
            }>
        ) {
            if (!draft.document) return;

            const { slideId, newIndex } = action.payload;
            const widgetInstances = draft.document.widgetInstances;
            const slides = widgetInstances.root.contentPlaceHolders.children || [];
            const currentIndex = slides.findIndex((slide: string) => slide === slideId);

            if (currentIndex === -1 || currentIndex === newIndex) return;
            const slideToMove = slides[currentIndex];

            slides.splice(currentIndex, 1);

            if (newIndex >= slides.length) {
                slides.push(slideToMove);
            } else {
                slides.splice(newIndex, 0, slideToMove);
            }

            draft.document.widgetInstances.root.contentPlaceHolders.children = [...slides];
        },
        SAVE_COMPONENT_PROPERTIES(
            draft,
            action: PayloadAction<{ id: string; properties: Record<string, unknown> }>
        ) {
            if (draft.document) {
                draft.document.widgetInstances[action.payload.id].properties =
                    action.payload.properties;
            } else {
                draft.document = null;
            }
        },
        SAVE_REFERENCE_COMPONENT_PROPERTIES(
            draft,
            action: PayloadAction<{
                id: string;
                refId: string;
                properties: Record<string, unknown>;
            }>
        ) {
            const { id, refId, properties } = action.payload;
            const widgetProperties = draft?.document?.widgetInstances[id]?.properties;
            const customizedProperties =
                draft?.document?.widgetInstances[refId]?.customizations?.[id]?.properties;
            const combinedProperties = { ...widgetProperties, ...customizedProperties };
            const updatedProperties = _.fromPairs(
                _.differenceWith(_.toPairs(properties), _.toPairs(combinedProperties), _.isEqual)
            );

            if (draft.document) {
                draft.document.widgetInstances[refId] = {
                    ...draft.document.widgetInstances[refId],
                    customizations: {
                        ...draft.document.widgetInstances[refId]?.customizations,
                        [id]: {
                            ...draft.document.widgetInstances[refId]?.customizations?.[id],
                            properties: {
                                ...customizedProperties,
                                ...updatedProperties,
                            },
                        },
                    },
                };
            } else {
                draft.document = null;
            }
        },

        SAVE_DOCUMENT(_draft, _action: PayloadAction<{ documentName: string }>) {},
        SAVE_DOCUMENT_FAILED(
            _draft,
            _action: PayloadAction<{ documentName: string; error: any }>
        ) {},
        SAVE_NEW_PAGE_START(draft) {
            draft.saveNewPageLoading = true;
            draft.pageSaveError = null;
            draft.pageSaveSuccess = null;
        },
        SAVE_NEW_PAGE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.saveNewPageLoading = false;
            draft.pageSaveSuccess = success;
            draft.pageSaveError = error;
        },
        NEWFOLDER_SAVE_START(draft) {
            draft.newFolderSaveLoading = true;
            draft.newFolderSaveError = null;
            draft.newFolderSaveSucess = null;
        },
        NEWFOLDER_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.newFolderSaveLoading = false;
            draft.newFolderSaveSucess = success || '';
            draft.newFolderSaveError = error;
        },
        FETCH_DOCUMENT(draft, _action: PayloadAction<void>) {
            draft.pageLoading = true;
        },
        FETCH_DOCUMENT_FAILED(draft, _action: PayloadAction<{ error: any }>) {
            draft.pageLoading = false;
        },

        RECEIVE_DOCUMENT(draft, action: PayloadAction<coreTypes.Document>) {
            draft.document = action.payload;
            draft.pageLoading = false;

            if (draft?.document) {
                const refComponents = _.pickBy(draft.document.widgetInstances, (v) => v.refLoaded);
                _.forEach(refComponents, (rc) => {
                    const customizations = rc?.customizations;
                    const customized = _.pickBy(customizations, (v) => v.contentPlaceHolders);
                    _.forEach(Object.keys(customized), (key: any) => {
                        if (draft?.document) {
                            draft.document.widgetInstances[key].contentPlaceHolders = {
                                ...draft.document.widgetInstances[key]?.contentPlaceHolders,
                                ...customized[key]?.contentPlaceHolders,
                            };
                        }
                    });
                });
            }
        },
        IS_DOCUMENT_LOADED(draft, action: PayloadAction<boolean>) {
            draft.isDocumentLoaded = action.payload;
        },
        EMPTY_DATA(draft, action) {
            draft.data = action.payload;
        },
        SET_PAGE_ACTION(draft, action: PayloadAction<string>) {
            draft.pageResourcesAction = action.payload;
        },
        SET_DOCUMENT_META_DATA(draft, action: PayloadAction<coreTypes.MetaDataOptions>) {
            draft.documentMetadata = {
                ...draft.documentMetadata,
                ...action.payload,
            };
        },
        SET_REFERENCE_COMPONENT(
            draft,
            action: PayloadAction<{ id: string; referenceComponent: coreTypes.ReferenceComponent }>
        ) {
            if (draft.document) {
                const docClass = new DocumentClass(draft.document);
                docClass.insertComponentsFromReference(
                    action.payload.id,
                    action.payload.referenceComponent
                );
            }
        },
        SET_EDITOR(draft, action: PayloadAction<coreTypes.Editor>) {
            draft.editor = action.payload;
        },
        SAVE_PAGE_RESOURCES(draft, action: PayloadAction<coreTypes.Resource>) {
            if (draft.document) {
                draft.document.widgetInstances.root.properties.locales = Object.keys(
                    action.payload
                );
                draft.document.widgetInstances.root.properties.resourceStrings = action.payload;
            }
        },
        SET_IS_SHOW_ANNOTATION_PICKER(draft, action: PayloadAction<boolean>) {
            draft.isShowAnnotationPicker = action.payload;
        },
        SET_SELECTED_ANNOTATION_PICKER_VALUE(draft, action: PayloadAction<string | null>) {
            draft.selectedAnnotationPickerValue = action.payload;
        },
        SET_IS_SHOW_DATA_PICKER(draft, action: PayloadAction<boolean>) {
            draft.isShowDataPicker = action.payload;
        },
        SET_IS_SHOW_FUNDS_PICKER(draft, action: PayloadAction<boolean>) {
            draft.isShowFundsPicker = action.payload;
        },
        ADD_BOTTOM_BAR_TAB(draft, action: PayloadAction<string>) {
            if (!draft.bottomBarTabs.includes(action.payload)) {
                draft.bottomBarTabs.push(action.payload);
            }
        },
        REMOVE_BOTTOM_BAR_TAB(draft, action: PayloadAction<string>) {
            const index = draft.bottomBarTabs.indexOf(action.payload);
            if (index > -1) {
                draft.bottomBarTabs.splice(index, 1);
            }
        },
        REMOVE_ALL_BOTTOM_BAR_TABS(draft) {
            draft.bottomBarTabs = [];
        },
        UPDATE_SLIDE_SNAPSHOT(draft, action: PayloadAction<any>) {
            const { id, snapshot } = action.payload;
            draft.snapshots = {
                ...draft.snapshots,
                [id]: snapshot,
            };
        },
    },
});

export const templateEditorReducerActions: any = templateSlice.actions;
export const templateEditorReducer = templateSlice.reducer;
